var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { cashflowTemplateData } from '@modules/cashflow/cf-template/models/data';
import usePublishTemplateMutate from '@modules/cashflow/cf-template/hooks/mutations/usePublishTemplateMutate';
import useCreateCFTemplateMutation from '@modules/cashflow/cf-template/hooks/mutations/useCreateCFTemplateMutation';
import useEditCFTemplateMutation from '@modules/cashflow/cf-template/hooks/mutations/useEditCFTemplateMutation';
import { CF_TEMPLATE_URL } from '@src/routes/url';
import useCustomTranslation from '@libs/hooks/useCustomTranslation';
/**
 * @description Controller for create template screen
 *
 * @author phamlam2k
 *
 */
var useCreateCashflowTemplateController = function () {
    // ---------------------------------
    // State and Form Initialization
    // ---------------------------------
    var id = useParams().id;
    var cfTemplate = useLoaderData();
    var navigate = useNavigate();
    var t = useCustomTranslation();
    var hotRef = useRef(null);
    var titleInputRef = useRef(null);
    var _a = useState(5), amountIndex = _a[0], setAmountIndex = _a[1];
    var _b = useState({}), requiredColumns = _b[0], setRequiredColumns = _b[1];
    // ---------------------------------
    // Query & Mutation
    // ---------------------------------
    var _c = useCreateCFTemplateMutation(), mutate = _c.mutate, isCreateTemplateLoading = _c.isPending;
    var _d = useEditCFTemplateMutation(), editTemplateMutate = _d.mutate, isEditTemplateLoading = _d.isPending;
    var publishMutate = usePublishTemplateMutate().mutate;
    // ---------------------------------
    // Memo hooks
    // ---------------------------------
    var listBtns = useMemo(function () {
        var _btns = [
            _jsx(Button, { variant: 'outlined', onClick: handleCancel, children: t('L000076') }),
            _jsx(Button, { variant: 'contained', onClick: handleSubmit, disabled: isCreateTemplateLoading || isEditTemplateLoading, children: t('L000075') })
        ];
        if (id) {
            _btns.push(_jsx(Button, { variant: 'outlined', onClick: handlePublish, children: t('L000123') }));
        }
        return _btns;
    }, [isCreateTemplateLoading, isEditTemplateLoading, handleSubmit, handlePublish]);
    // ---------------------------------
    // Effects
    // ---------------------------------
    useEffect(function () {
        if (hotRef && hotRef.current) {
            hotRef.current.hotInstance.loadData(cloneDeep(cashflowTemplateData));
        }
        if (!cfTemplate)
            return;
        if (cfTemplate) {
            setAmountIndex(cfTemplate.CashflowContent.amountIndex);
        }
        return function () {
            hotRef.current = null;
        };
    }, [cfTemplate]);
    // ---------------------------------
    // Functions
    // ---------------------------------
    var handleCheckRequiredColumnsState = function (defaultValue) {
        setRequiredColumns(defaultValue);
    };
    // Check columns is required or not
    function handleCheckRequiredColumns(event, col) {
        event.stopPropagation();
        event.preventDefault();
        var hotInstance = hotRef.current.hotInstance;
        var _data = hotInstance.getData();
        setTimeout(function () {
            if (hotInstance) {
                hotRef.current.hotInstance.loadData(cloneDeep(_data));
            }
        }, 1);
        setRequiredColumns(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[col] = event.target.checked, _a)));
        });
    }
    var handleCheckRequiredColumnsAfterDrop = function (currentCol, newCol) {
        var _a;
        setRequiredColumns(function (prev) {
            var updatedColumns = __assign({}, prev);
            if (currentCol < newCol) {
                for (var i = currentCol; i < newCol; i++) {
                    updatedColumns[i] = updatedColumns[i + 1];
                }
            }
            else {
                for (var i = currentCol; i > newCol; i--) {
                    updatedColumns[i] = updatedColumns[i - 1];
                }
            }
            updatedColumns[newCol] = true;
            return updatedColumns;
        });
        if (currentCol === amountIndex) {
            setAmountIndex(newCol);
        }
        var hotInstance = (_a = hotRef.current) === null || _a === void 0 ? void 0 : _a.hotInstance;
        if (!hotInstance)
            return;
        var _data = hotInstance === null || hotInstance === void 0 ? void 0 : hotInstance.getData();
        setTimeout(function () {
            if (hotInstance) {
                hotRef.current.hotInstance.loadData(cloneDeep(_data));
            }
        }, 1);
    };
    // Back to Cashflow Template screen
    function handleCancel() {
        navigate(CF_TEMPLATE_URL);
    }
    // Save draft data
    function handlePublish() {
        publishMutate(id, {
            onSuccess: function () {
                navigate(CF_TEMPLATE_URL);
            }
        });
    }
    var saveData = function () {
        var _requireColumns = Object.keys(requiredColumns).reduce(function (acc, key) {
            if (requiredColumns[key]) {
                acc.push(parseInt(key));
            }
            return acc;
        }, []);
        if (id) {
            editTemplateMutate({
                id: id,
                data: {
                    name: titleInputRef.current.value,
                    content: hotRef.current.hotInstance.getData(),
                    requireColumns: _requireColumns,
                    amountIndex: amountIndex
                }
            });
        }
        else {
            mutate({
                name: titleInputRef.current.value,
                content: hotRef.current.hotInstance.getData(),
                requireColumns: _requireColumns,
                amountIndex: amountIndex
            }, {
                onError: function () {
                    setTimeout(function () {
                        hotRef.current.hotInstance.loadData(cloneDeep(hotRef.current.hotInstance.getData()));
                    }, 2000);
                }
            });
        }
    };
    // Save data
    function handleSubmit() {
        if (!titleInputRef.current.value) {
            titleInputRef.current.focus();
            return;
        }
        validateAllCells(saveData);
    }
    var clearAllComments = function () {
        var hotInstance = hotRef.current.hotInstance;
        hotInstance.getData().forEach(function (row, rowIndex) {
            row.forEach(function (_, colIndex) {
                hotInstance.setCellMeta(rowIndex, colIndex, 'comment', null);
            });
        });
        hotInstance.render();
    };
    function validateAllCells(callBack) {
        var hotInstance = hotRef.current.hotInstance;
        clearAllComments();
        hotInstance.validateCells(function (valid) {
            if (!valid) {
                var invalidCells_1 = [];
                hotInstance.getData().forEach(function (row, rowIndex) {
                    row.forEach(function (_, colIndex) {
                        var meta = hotInstance.getCellMeta(rowIndex, colIndex);
                        if (meta.valid === false) {
                            invalidCells_1.push({ row: rowIndex, col: colIndex });
                        }
                    });
                });
                if (invalidCells_1.length > 0) {
                    var firstInvalidCell = invalidCells_1[0];
                    hotInstance.selectCell(firstInvalidCell.row, firstInvalidCell.col);
                    hotInstance.setCellMeta(firstInvalidCell.row, firstInvalidCell.col, 'comment', {
                        value: 'Please enter a valid number.'
                    });
                    hotInstance.getPlugin('comments').showAtCell(firstInvalidCell.row, firstInvalidCell.col);
                }
            }
            else {
                callBack();
            }
        });
    }
    return {
        hotRef: hotRef,
        listBtns: listBtns,
        amountIndex: amountIndex,
        titleInputRef: titleInputRef,
        requiredColumns: requiredColumns,
        handleCheckRequiredColumns: handleCheckRequiredColumns,
        handleCheckRequiredColumnsState: handleCheckRequiredColumnsState,
        handleCheckRequiredColumnsAfterDrop: handleCheckRequiredColumnsAfterDrop
    };
};
export default useCreateCashflowTemplateController;
